'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { VERIFICATION_COPY } from '@/lib/features';

// ⚠️ These were invented figures from the site generator — "182,400+ Registered
// Farmers", "£2.4B+ Total Trade Value" and so on — on a platform that has just
// launched. Publishing numbers we cannot evidence is a misleading-advertising
// problem, so they have been replaced with claims that are true on day one.
// Put real figures back here once there are real figures to quote.
const stats = [
  { value: '0%', label: 'Commission on sales' },
  { value: 'Direct', label: 'Farmer to farmer' },
  { value: 'UK-wide', label: 'Buyers and sellers' },
  { value: 'Verified', label: 'Every seller checked' },
];

// ⚠️ PLACEHOLDER DATA — DO NOT PUBLISH AS-IS
// "James Whitfield" and "Sarah Donnelly" are not real customers and never said
// this. Quoting invented testimonials from named farmers is a misleading-
// advertising problem and would be embarrassing if spotted. Emptied for now;
// paste real quotes in here (with the customer's permission) and they reappear.
const testimonials: {
  id: string;
  name: string;
  farm: string;
  text: string;
  avatar: string;
}[] = [];

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] hero-gradient flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-10 w-64 h-64 rounded-full border-4 border-white" />
          <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full border-4 border-white" />
          <div className="absolute top-1/2 left-1/2 w-48 h-48 rounded-full border-2 border-white" />
        </div>

        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-3 mb-10">
            <AppLogo size={44} />
            <div>
              <div className="font-extrabold text-xl text-white">FarmTradeDirect</div>
              <div className="text-green-300 text-xs font-medium tracking-wide">UK&apos;s Most Complete Agricultural Marketplace</div>
            </div>
          </Link>

          <h1 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
            Trade Livestock, Machinery &amp; More — Direct.
          </h1>
          <p className="text-green-200 text-base leading-relaxed mb-8 max-w-md">
            Post listings, bid on haulage jobs, and access monthly timed auctions — all in one place. No commission, no middleman.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {stats?.map((stat) => (
              <div key={`stat-${stat?.label}`} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="text-2xl font-extrabold text-white tabular-nums">{stat?.value}</div>
                <div className="text-green-300 text-xs font-medium mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="flex flex-col gap-3">
            {testimonials?.map((t) => (
              <div key={t?.id} className="bg-white/8 rounded-xl p-4 backdrop-blur-sm border border-white/10">
                <p className="text-green-100 text-sm leading-relaxed italic mb-3">&quot;{t?.text}&quot;</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">
                    {t?.avatar}
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">{t?.name}</div>
                    <div className="text-green-400 text-xs">{t?.farm}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 mt-8">
          <Icon name="ShieldCheckIcon" size={18} className="text-green-300" />
          <span className="text-green-300 text-xs">{VERIFICATION_COPY.short} · Secure Stripe Payments · GDPR Compliant</span>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-10 bg-background overflow-y-auto">
        {/* Mobile logo */}
        <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={36} />
          <span className="font-extrabold text-lg text-primary">FarmTradeDirect</span>
        </Link>

        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'login' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'register' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {activeTab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}